import { Clock, GraduationCap, CupSoda, Phone } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "../../lib/lang";
import { FULL_MENU, INFO } from "../../lib/content";

const TXT = {
  sv: {
    eyebrow: "Lunch & student", title: "Valfri rulle,", em: "ett pris",
    lunch: "Lunchmeny", lunchSub: "Valfri rulle med pommes och dryck.", time: "Lunch 10:00–14:00",
    l2: "Rulle + pommes + dryck",
    student: "Studentmeny", studentSub: "Valfri rulle, eller med pommes och valfri läsk.",
    s0: "Valfri rulle", s1: "Rulle + pommes + valfri läsk", noId: "Inget studentkort behövs",
    incl: ["Valfri rulle", "Pommes", "Valfri läsk"],
    choose: "Lunch: välj bland alla våra rullar", studentChoose: "Rullar i studentmenyn", call: "Ring & beställ",
  },
  en: {
    eyebrow: "Lunch & student", title: "Any wrap,", em: "one price",
    lunch: "Lunch menu", lunchSub: "Any wrap with fries and a drink.", time: "Lunch 10:00–14:00",
    l2: "Wrap + fries + drink",
    student: "Student menu", studentSub: "Any wrap, or with fries and any soft drink.",
    s0: "Any wrap", s1: "Wrap + fries + any soft drink", noId: "No student card needed",
    incl: ["Any wrap", "Fries", "Any soft drink"],
    choose: "Lunch: choose from all our wraps", studentChoose: "Wraps on the student menu", call: "Call to order",
  },
};

const Row = ({ label, price, light, testId }) => (
  <div data-testid={testId} className={`flex items-baseline gap-3 border-b border-dashed py-4 ${light ? "border-white/25" : "border-[#721121]/25"}`}>
    <span className={`text-base ${light ? "text-white/90" : "text-[#1C1917]"}`}>{label}</span>
    <span className="flex-1" />
    <span className={`font-display text-4xl font-semibold ${light ? "text-[#E9C46A]" : "text-[#721121]"}`}>{price} kr</span>
  </div>
);

const Stack = ({ srcs, testId, ring }) => (
  <div data-testid={testId} className="mt-6 flex items-center">
    {srcs.map((s, i) => (
      <img key={s} src={s} alt="" loading="lazy" style={{ zIndex: srcs.length - i }} className={`-ml-4 h-16 w-16 rounded-full object-cover object-top shadow-lg ring-[3px] transition-transform duration-300 first:ml-0 hover:-translate-y-1 sm:h-20 sm:w-20 ${ring}`} />
    ))}
  </div>
);

export const LunchStudent = () => {
  const { lang } = useLang();
  const t = TXT[lang];
  const wraps = FULL_MENU.find((c) => c.id === "rullar").items;
  const wrapPics = wraps.filter((w) => w[2]).slice(0, 6).map((w) => `/${w[2]}-lg.webp`);
  const studentWraps = wraps.filter((w) => !["Nötshawarma", "Kofta"].includes(w[0]));
  const studentPics = studentWraps.filter((w) => w[2]).slice(0, 4).map((w) => `/${w[2]}-lg.webp`);
  return (
    <div data-testid="lunch-student-section" className="mb-16">
      <Reveal>
        <p className="eyebrow text-[#721121]">{t.eyebrow}</p>
        <h3 className="font-display mt-4 text-4xl leading-[0.95] tracking-tight text-[#1C1917] sm:text-5xl">
          {t.title} <em className="italic text-[#721121]">{t.em}</em>
        </h3>
      </Reveal>
      <div className="mt-10 grid gap-4 lg:grid-cols-2">
        <Reveal data-testid="lunch-menu-card" className="relative overflow-hidden rounded-[28px] bg-[#721121] p-8 text-white sm:p-10">
          <div className="pattern-overlay pointer-events-none absolute inset-0 opacity-[0.08]" />
          <div className="relative">
            <Clock className="text-[#E9C46A]" size={26} strokeWidth={1.5} />
            <h4 className="font-display mt-4 text-4xl">{t.lunch}</h4>
            <p className="mt-1 text-sm text-white/80">{t.lunchSub}</p>
            <span data-testid="lunch-time-badge" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#E9C46A] px-4 py-1.5 text-sm font-semibold text-[#2a0a10]"><Clock size={14} /> {t.time}</span>
            <Stack srcs={wrapPics} testId="lunch-wrap-photos" ring="ring-[#721121]" />
            <div className="mt-6">
              <Row light label={t.l2} price="99" testId="lunch-combo-price" />
            </div>
            <ul className="mt-5 flex flex-wrap gap-2">
              {(lang === "sv" ? ["Valfri rulle", "Pommes", "Dryck"] : ["Any wrap", "Fries", "Drink"]).map((x) => (
                <li key={x} className="rounded-full bg-white/10 px-4 py-2 text-sm text-white ring-1 ring-white/25">+ {x}</li>
              ))}
            </ul>
            <div className="hidden">
            </div>
          </div>
        </Reveal>
        <Reveal delay={0.08} data-testid="student-menu-card" className="relative overflow-hidden rounded-[28px] bg-[#FBF1E6]/85 p-8 ring-1 ring-[#A9493B]/20 backdrop-blur-md sm:p-10">
          <span className="absolute right-6 top-6 rounded-full bg-[#E9C46A] px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-[#2a0a10]">{t.noId}</span>
          <GraduationCap className="text-[#721121]" size={28} strokeWidth={1.5} />
          <h4 className="font-display mt-4 text-4xl text-[#1C1917]">{t.student}</h4>
          <p className="mt-1 flex items-center gap-2 text-sm text-[#57534E]"><CupSoda size={15} className="text-[#721121]" /> {t.studentSub}</p>
          <div className="flex items-center gap-3">
            <Stack srcs={studentPics} testId="student-wrap-photos" ring="ring-[#F6EFE4]" />
            <span className="mt-6 font-display text-3xl text-[#721121]">+</span>
            <img data-testid="student-pommes-photo" src="/dish/o-chips.webp" alt="Pommes" className="mt-6 h-16 w-16 rounded-full object-cover shadow-lg ring-[3px] ring-[#F6EFE4] sm:h-20 sm:w-20" />
          </div>
          <div className="mt-6">
            <Row label={t.s0} price="60" testId="student-wrap-price" />
            <Row label={t.s1} price="85" testId="student-combo-price" />
          </div>
          <ul className="mt-5 flex flex-wrap gap-2">
            {t.incl.map((x) => (
              <li key={x} className="rounded-full bg-white px-4 py-2 text-sm text-[#1C1917] ring-1 ring-[#721121]/15">+ {x}</li>
            ))}
          </ul>
          <p className="mt-6 text-[11px] uppercase tracking-[0.2em] text-[#721121]">{t.studentChoose}</p>
          <p data-testid="student-wrap-list" className="mt-2 text-sm leading-relaxed text-[#1C1917]">{studentWraps.map((w) => w[0]).join(" · ")}</p>
        </Reveal>
      </div>
      <Reveal className="mt-6 flex flex-col gap-4 rounded-[24px] bg-white p-6 ring-1 ring-[#721121]/10 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#721121]">{t.choose}</p>
          <p data-testid="lunch-wrap-list" className="mt-2 text-sm leading-relaxed text-[#1C1917]">{wraps.map((w) => w[0]).join(" · ")}</p>
        </div>
        <a data-testid="lunch-call-button" href={INFO.phoneHref} className="btn-wine shrink-0 justify-center"><Phone size={16} /> {t.call}</a>
      </Reveal>
    </div>
  );
};
